// The session, backed by the server (ICD-S03) rather than by localStorage.
//
// WHAT CHANGED AND WHY IT IS NOT A SIMPLE PORT. Until 2026-09-14 this file
// invented an account: `signUp` minted a `userId` in the browser and wrote it
// to localStorage, and `signIn` handed back a fixed demo identity because
// there was nothing to check a password against. Now there is. The id comes
// from the server, the password is real, and the thing that proves who you
// are is an **HttpOnly cookie this code cannot read** — which is the point of
// it being HttpOnly.
//
// THE THIRD STATE, AND HOW IT IS HIDDEN. Asking "who am I" is now a round
// trip where it used to be a synchronous read, so between *signed out* and
// *signed in* there is a moment of *don't know yet*. Sixteen screens read
// `session` and none of them have ever had to handle that. So the client
// keeps a **non-secret copy of the display identity** —
// nothing but the id, the name, the avatar and the email — so the shell
// paints instantly on a cold load, then reconciles against `GET /api/me` and
// corrects itself if the server disagrees. A returning visitor sees no
// flash; somebody whose session expired sees their name for a moment and
// then the front door.
//
// The CACHE IS NOT A CREDENTIAL. It cannot authorize anything: every request
// is judged by the cookie, and a browser holding a stale cache gets a 401
// from the first call it makes. Forging it buys a name in a header bar for
// one paint and nothing else.
//
// A FAILED FETCH IS NOT A SIGN-OUT. If `/api/me` answers 401 we know we are
// signed out and the cache is cleared. If the fetch THROWS — the dev server
// is not running, the network dropped, the laptop woke up in a tunnel — we
// know nothing, so the cached identity stays and `reconciled` stays false.
// Treating an unreachable server as "signed out" is the silent fallback the
// house rules forbid: it would log people out of a working session because
// of a blip.

import { forgetThisDevice } from './push';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { z } from 'zod';
import {
  AvatarRef,
  EmailChangePending,
  EmailChanged,
  EmailConfirmed,
  MeView,
  type ErrorCode,
  type Face,
} from '@fsp/domain';
import { get, messageOf, send } from './api';
import { newsOfMe, publishMe } from './meFeed';
import { onDevice } from './keptIdb';
import { forgetReads } from './read';
import { storageKey } from './storage';

export interface Session {
  userId: string;
  displayName: string;
  /** The address this account signs in with. Read-only here; see `update`. */
  email: string;
  /**
   * Whether that address is confirmed (a confirm link or a reset link was
   * followed), which is when email other than account mail reaches it
   * (PLAN-2026-10-18-families-and-newcomers §7.1). Optional: a session cached
   * before 2026-10-19 has no word on it until `/api/me` answers.
   */
  emailConfirmed?: boolean;
  /**
   * The address a change waits to move the account to, until the link sent
   * there is used (PLAN-2026-10-28-by-email §3.5); null with none. Optional:
   * a cached session has no word on it until `/api/me` answers.
   */
  emailChangingTo?: string | null;
  /**
   * The IANA zone every time this person reads is told in (B-181): the
   * account's, chosen at sign-up and changed on the Account page. Null for an
   * account made before it could be chosen — the app asks them to pick one.
   * Never read from the device after sign-up: a phone that travels keeps the
   * account's zone.
   */
  timeZone: string | null;
  /**
   * Whether the simulated world is visible.
   *
   * CLIENT-ONLY, deliberately. It is not a field on the account and never
   * goes to the server: the tutorial/demo as it stands does not drive
   * backend decisions. So this stays a switch on this device
   * about what this browser shows, which is all it has ever been.
   */
  demo: boolean;
  /**
   * The generated avatar's seed, as a number, because that is what the
   * screens draw with.
   *
   * The contract carries `AvatarRef.seed` as a STRING (it also has to name
   * an uploaded asset one day). Numbers round-trip as their own digits; a
   * seeded account whose seed is a slug like `ken` is hashed to a hue
   * instead, so every account has a stable color either way.
   */
  avatarSeed: number;
  /**
   * The face the account wears instead of its initial, on its disc's two
   * colors (PLAN-2026-10-24-faces-round-three §7); null for the initial.
   * Optional: a session cached before 2026-10-24 has none until `/api/me`
   * answers.
   */
  avatarFace?: AvatarFace | null;
}

/** A face an account wears, and the two colors of the disc it is drawn on. */
export interface AvatarFace {
  face: Face;
  colors: { primary: string; secondary: string };
}

/** An account's face from what the wire says, or null for any other avatar. */
function avatarFaceOf(avatar: unknown): AvatarFace | null {
  const parsed = AvatarRef.safeParse(avatar);
  return parsed.success && parsed.data.kind === 'face'
    ? { face: parsed.data.face, colors: parsed.data.colors }
    : null;
}

// The detail level used to live here as `proMode: boolean`. It is a three-stop
// range now and belongs to the person rather than the account, so a visitor in
// the tutorial can set it too: see data/detail.tsx, which also migrates anyone
// who had Pro mode on.

/**
 * v2, and the bump matters.
 *
 * v1 held a `userId` this code made up. Reading one now would put a
 * fabricated id in the header bar until the first reconcile, and on a
 * machine that never reaches the server it would sit there indefinitely.
 * A new key means every v1 session is simply absent, which is the truth.
 */
const KEY = storageKey('session.v2');

/**
 * The one thing about this device the server cannot read (`ClientHints.form`):
 * an iPad's Safari has said "Macintosh" since iPadOS 13, and only the page can
 * see the touch screen. Everything else the server tells from the agent
 * (`formOf`), so this says nothing unless it knows better. For the activity
 * log's `session.started` (A2).
 */
function formHint(): { form?: 'tablet' } {
  try {
    return /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1
      ? { form: 'tablet' }
      : {};
  } catch {
    return {};
  }
}
/** Client-only, and kept apart from the identity so it survives a sign-out. */
const DEMO_KEY = storageKey('demo.v1');

/** The generated avatar's color. Same seed, same color, on every device. */
export function avatarColor(seed: number): string {
  return `hsl(${Math.abs(seed) % 360} 62% 42%)`;
}

/** The same color as six hex digits, for a face's disc, which a team's colors are. */
export function avatarHex(seed: number): string {
  const h = Math.abs(seed) % 360;
  const s = 0.62;
  const l = 0.42;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const hex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${hex(f(0))}${hex(f(8))}${hex(f(4))}`;
}

export function randomSeed(): number {
  return Math.floor(Math.random() * 360);
}

/**
 * The contract's string seed as the number the screens draw with.
 *
 * `'210'` is 210. `'ken'` — what the seed script writes — is hashed, so the
 * seeded accounts have a stable color rather than all sharing a default.
 */
export function seedToNumber(seed: string): number {
  if (/^\d+$/.test(seed)) return Number(seed) % 360;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) % 360;
  return hash;
}

function readDemo(): boolean {
  try {
    return localStorage.getItem(DEMO_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDemo(on: boolean): void {
  try {
    if (on) localStorage.setItem(DEMO_KEY, '1');
    else localStorage.removeItem(DEMO_KEY);
  } catch {
    // Storage blocked: the choice lasts for this page load.
  }
}

/** The cached display identity. Never the token, which this code cannot see. */
function parse(raw: string | null): Omit<Session, 'demo'> | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<Session>;
    if (typeof v.userId !== 'string' || typeof v.displayName !== 'string') return null;
    return {
      userId: v.userId,
      displayName: v.displayName,
      email: typeof v.email === 'string' ? v.email : '',
      timeZone: typeof v.timeZone === 'string' ? v.timeZone : null,
      avatarSeed: typeof v.avatarSeed === 'number' ? v.avatarSeed : 210,
      avatarFace: v.avatarFace ? avatarFaceOf({ kind: 'face', ...v.avatarFace }) : null,
    };
  } catch {
    return null;
  }
}

function readCache(): Omit<Session, 'demo'> | null {
  try {
    return parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

function writeCache(session: Omit<Session, 'demo'> | null): void {
  try {
    if (session) localStorage.setItem(KEY, JSON.stringify(session));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage blocked: the shell will simply paint a moment later next time.
  }
}

/** `MeView` as the shape the screens read. */
function fromView(view: MeView): Omit<Session, 'demo'> {
  return {
    userId: view.user.id,
    displayName: view.user.displayName,
    email: view.logins.find((l) => l.kind === 'password')?.identifier ?? '',
    emailConfirmed: view.logins.some((l) => l.kind === 'password' && l.verified),
    emailChangingTo: view.logins.find((l) => l.kind === 'password')?.changingTo ?? null,
    timeZone: view.user.timeZone ?? null,
    // A face keeps the initial's seed beside it, so the color comes back with it.
    avatarSeed:
      view.user.avatar.kind === 'generated'
        ? seedToNumber(view.user.avatar.seed)
        : view.user.avatar.kind === 'face' && view.user.avatar.seed !== undefined
          ? seedToNumber(view.user.avatar.seed)
          : 210,
    avatarFace: avatarFaceOf(view.user.avatar),
  };
}

/**
 * `code` is the server's, when it refused: a screen that does something
 * different for an expired link than for a short password reads it.
 */
export type Outcome = { ok: true } | { ok: false; message: string; code?: ErrorCode };

/**
 * Nothing worth parsing.
 *
 * Signing out and closing the account both answer `{ data: {} }`: what
 * matters is that the server agreed, and there is no shape to check.
 */
const Nothing = z.unknown();

// The envelope handling that used to live here — `credentials`, the non-JSON
// answer, the status codes, the `{data} | {error}` split — is `data/api.ts`
// now. It was written here first, correctly, and then written four more times
// elsewhere; this file was the seed for the shared one rather than its
// exception.

interface SessionContextValue {
  session: Session | null;
  /**
   * False until the first `GET /api/me` has COMPLETED — answered, refused, or
   * failed to reach the server at all.
   *
   * "Completed" and not "succeeded", deliberately. A router needs to know
   * whether we are still finding out, and if this stayed false when the
   * server is unreachable then a signed-out visitor would wait forever on a
   * blank screen instead of being shown the front door. Best attempt made is
   * the most any caller can act on.
   */
  reconciled: boolean;
  signUp: (input: {
    displayName: string;
    email: string;
    password: string;
    timeZone: string;
  }) => Promise<Outcome>;
  signIn: (input: { email: string; password: string; remember?: boolean }) => Promise<Outcome>;
  /**
   * Ask for an emailed link to choose a new password (B-306).
   *
   * `ok` means the site took the request, NOT that an account exists: the
   * server answers the same for every address, and so does every screen that
   * says what happens next.
   */
  requestReset: (email: string) => Promise<Outcome>;
  /** The token from that link and a new password: signs this device in, and every other one out. */
  confirmReset: (token: string, password: string) => Promise<Outcome>;
  /**
   * Ask for a sign-in code by email (PLAN-2026-10-28-by-email §3.4). `ok`
   * means the site took the request, not that an account exists, as a
   * reset's does.
   */
  requestCode: (email: string) => Promise<Outcome>;
  /** The address and the six digits from that email: signs this device in. */
  signInWithCode: (input: { email: string; code: string; remember?: boolean }) => Promise<Outcome>;
  /** The token from that email's link: signs in whichever browser opened it. */
  signInWithLink: (token: string) => Promise<Outcome>;
  /**
   * Ask to change the address this account signs in with (§3.5): the new one
   * and the current password. `ok` means the link went to the new address;
   * the account is read again, so the page shows the change waiting.
   */
  changeEmail: (input: { email: string; password: string }) => Promise<Outcome>;
  /** Take back a change still waiting on its link. */
  cancelEmailChange: () => Promise<Outcome>;
  /** The new address's link: the change made, and where the account signs in now. */
  confirmEmailChange: (
    token: string,
  ) => Promise<{ ok: true; to: string } | { ok: false; message: string; code?: ErrorCode }>;
  /** The old address's undo and a new password: signs this device in, and every other one out. */
  undoEmailChange: (token: string, password: string) => Promise<Outcome>;
  /** Email me a link that confirms my address (§7.1). `conflict` when it is confirmed already. */
  askConfirmLink: () => Promise<Outcome>;
  /**
   * The token from a confirm link. Signed in or not; signed in, the account is
   * read again so every screen knows the address is confirmed.
   */
  confirmEmail: (
    token: string,
  ) => Promise<{ ok: true; already: boolean } | { ok: false; message: string; code?: ErrorCode }>;
  /**
   * Change the profile.
   *
   * `displayName` and `avatarSeed` go to the server. `demo` is client-only
   * and never leaves this device. **`email` changes through `changeEmail`**,
   * behind the password and a link to the new address, never here.
   */
  update: (
    patch: Partial<Pick<Session, 'displayName' | 'demo' | 'avatarSeed' | 'avatarFace'>> & {
      timeZone?: string;
    },
  ) => Promise<Outcome>;
  signOut: () => Promise<void>;
  /**
   * Delete the account (ACCOUNTS.md rule 6), server-side and on this device.
   *
   * `password` is the account's, as the person typed it. The server checks
   * it and refuses without it — the screen asking is not the same as the
   * endpoint requiring, and `DELETE /api/me` is reachable without a screen
   * (PLAN-2026-10-31-deleting-an-account). A wrong one comes back with
   * `code: 'forbidden'`, to be said under the field.
   */
  deleteAccount: (password: string) => Promise<Outcome>;
  /**
   * Drop the cached display identity, because the server has stopped
   * honoring this session.
   *
   * Exposed as of 2026-09-17 for the one caller that finds out before this
   * file does: the hub read is the first request most pages make, so a
   * session revoked from another device shows up there. Without it the
   * browser kept a name in the header bar for an account it could no longer
   * act as until something else happened to ask.
   *
   * It signs nobody out at the server — there is nothing left to sign out of.
   */
  forget: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  // WHOSE READS THIS DEVICE KEEPS (B-557, `kept.ts`) is said before any
  // screen reads: the cached identity is the account the cookie last was.
  const [identity, setIdentity] = useState<Omit<Session, 'demo'> | null>(() => {
    const cached = readCache();
    onDevice.setAccount(cached?.userId ?? null);
    return cached;
  });
  const [demo, setDemo] = useState<boolean>(readDemo);
  const [reconciled, setReconciled] = useState(false);

  /**
   * Take the server's answer. `askedAt` is when the request left: the answer
   * also carries the account's choices (`MeView.prefs`), and `meFeed.ts`
   * hands it on to the code that keeps them in step with this device.
   */
  const adopt = useCallback((view: MeView, askedAt: number) => {
    const next = fromView(view);
    // Somebody else than the device last kept for: what it kept is theirs.
    if (onDevice.account() !== null && onDevice.account() !== next.userId) void onDevice.forget();
    onDevice.setAccount(next.userId);
    writeCache(next);
    setIdentity(next);
    publishMe(newsOfMe(view), askedAt);
  }, []);

  const forget = useCallback(() => {
    writeCache(null);
    setIdentity(null);
    // Nothing read as the account that just left is drawn for whoever is
    // next, nor kept on this device for them (B-557).
    onDevice.setAccount(null);
    forgetReads();
  }, []);

  // Reconcile once on mount. The cached identity is already on screen by the
  // time this runs, which is the whole point of caching it.
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    void (async () => {
      const askedAt = Date.now();
      const reply = await get('/me', MeView);
      if (reply.status === 'ok') adopt(reply.data, askedAt);
      // A refusal is a DEFINITE answer, so the cache goes. Unreachable is
      // not: we learned nothing, and the cached identity stays exactly as it
      // is rather than logging somebody out of a working session over a blip.
      else if (
        reply.status === 'refused' &&
        (reply.code === 'unauthorized' || reply.code === 'forbidden')
      )
        forget();
      // Either way the attempt is over, and anything waiting on us has to be
      // released or it waits for the rest of the session.
      setReconciled(true);
    })();
  }, [adopt, forget]);

  const enter = useCallback(
    async (path: string, body: unknown): Promise<Outcome> => {
      const askedAt = Date.now();
      const reply = await send('POST', path, body, MeView);
      if (reply.status === 'ok') {
        // A new sign-in may be somebody else: what was read before it goes.
        forgetReads();
        adopt(reply.data, askedAt);
        setReconciled(true);
        return { ok: true };
      }
      return {
        ok: false,
        message: messageOf(
          reply,
          'That did not work. Try again.',
          'Could not reach the site. Check your connection.',
        ),
        ...(reply.status === 'refused' ? { code: reply.code } : {}),
      };
    },
    [adopt],
  );

  const signUp = useCallback<SessionContextValue['signUp']>(
    ({ displayName, email, password, timeZone }) => {
      // A clean start (DEMO.md): a new account lands on an empty hub, so the
      // first league it shows is one the person actually made.
      writeDemo(false);
      setDemo(false);
      return enter('/auth/signup', {
        displayName: displayName.trim(),
        email: email.trim(),
        password,
        timeZone,
        ...formHint(),
      });
    },
    [enter],
  );

  const signIn = useCallback<SessionContextValue['signIn']>(
    ({ email, password, remember }) =>
      enter('/auth/login', {
        email: email.trim(),
        password,
        remember: remember ?? false,
        ...formHint(),
      }),
    [enter],
  );

  const requestReset = useCallback<SessionContextValue['requestReset']>(async (email) => {
    const reply = await send('POST', '/auth/reset/request', { email: email.trim() }, Nothing);
    if (reply.status === 'ok') return { ok: true };
    return {
      ok: false,
      message: messageOf(
        reply,
        'That did not work. Try again.',
        'Could not reach the site. Check your connection.',
      ),
    };
  }, []);

  const confirmReset = useCallback<SessionContextValue['confirmReset']>(
    (token, password) => enter('/auth/reset/confirm', { token, password, ...formHint() }),
    [enter],
  );

  const requestCode = useCallback<SessionContextValue['requestCode']>(async (email) => {
    const reply = await send('POST', '/auth/magic/request', { email: email.trim() }, Nothing);
    if (reply.status === 'ok') return { ok: true };
    return {
      ok: false,
      message: messageOf(
        reply,
        'That did not work. Try again.',
        'Could not reach the site. Check your connection.',
      ),
    };
  }, []);

  const signInWithCode = useCallback<SessionContextValue['signInWithCode']>(
    ({ email, code, remember }) =>
      enter('/auth/magic/confirm', {
        email: email.trim(),
        code: code.trim(),
        remember: remember ?? false,
        ...formHint(),
      }),
    [enter],
  );

  const signInWithLink = useCallback<SessionContextValue['signInWithLink']>(
    (token) => enter('/auth/magic/confirm', { token, ...formHint() }),
    [enter],
  );

  /** `/api/me` again, for a change the account page should show at once. */
  const reread = useCallback(async () => {
    const askedAt = Date.now();
    const me = await get('/me', MeView);
    if (me.status === 'ok') adopt(me.data, askedAt);
  }, [adopt]);

  const changeEmail = useCallback<SessionContextValue['changeEmail']>(
    async ({ email, password }) => {
      const reply = await send(
        'PUT',
        '/me/email',
        { email: email.trim(), password },
        EmailChangePending,
      );
      if (reply.status === 'ok') {
        await reread();
        return { ok: true };
      }
      return {
        ok: false,
        message: messageOf(
          reply,
          'That did not work. Try again.',
          'Could not reach the site. Check your connection.',
        ),
        ...(reply.status === 'refused' ? { code: reply.code } : {}),
      };
    },
    [reread],
  );

  const cancelEmailChange = useCallback<SessionContextValue['cancelEmailChange']>(async () => {
    const reply = await send('DELETE', '/me/email', undefined, Nothing);
    if (reply.status === 'ok') {
      await reread();
      return { ok: true };
    }
    return {
      ok: false,
      message: messageOf(
        reply,
        'That did not work. Try again.',
        'Could not reach the site. Check your connection.',
      ),
    };
  }, [reread]);

  const confirmEmailChange = useCallback<SessionContextValue['confirmEmailChange']>(
    async (token) => {
      const reply = await send('POST', '/auth/email/change', { token }, EmailChanged);
      if (reply.status === 'ok') {
        if (identity !== null) await reread();
        return { ok: true, to: reply.data.to };
      }
      return {
        ok: false,
        message: messageOf(
          reply,
          'That did not work. Try again.',
          'Could not reach the site. Check your connection.',
        ),
        ...(reply.status === 'refused' ? { code: reply.code } : {}),
      };
    },
    [identity, reread],
  );

  const undoEmailChange = useCallback<SessionContextValue['undoEmailChange']>(
    (token, password) => enter('/auth/email/undo', { token, password, ...formHint() }),
    [enter],
  );

  const askConfirmLink = useCallback<SessionContextValue['askConfirmLink']>(async () => {
    const reply = await send('POST', '/me/email/confirm', {}, Nothing);
    if (reply.status === 'ok') return { ok: true };
    return {
      ok: false,
      message: messageOf(
        reply,
        'That did not work. Try again.',
        'Could not reach the site. Check your connection.',
      ),
      ...(reply.status === 'refused' ? { code: reply.code } : {}),
    };
  }, []);

  const confirmEmail = useCallback<SessionContextValue['confirmEmail']>(
    async (token) => {
      const reply = await send('POST', '/auth/email/confirm', { token }, EmailConfirmed);
      if (reply.status === 'ok') {
        if (identity !== null) {
          const askedAt = Date.now();
          const me = await get('/me', MeView);
          if (me.status === 'ok') adopt(me.data, askedAt);
        }
        return { ok: true, already: reply.data.already };
      }
      return {
        ok: false,
        message: messageOf(
          reply,
          'That did not work. Try again.',
          'Could not reach the site. Check your connection.',
        ),
        ...(reply.status === 'refused' ? { code: reply.code } : {}),
      };
    },
    [identity, adopt],
  );

  const update = useCallback<SessionContextValue['update']>(
    async (patch) => {
      if (patch.demo !== undefined) {
        writeDemo(patch.demo);
        setDemo(patch.demo);
      }
      const wantsProfile =
        patch.displayName !== undefined ||
        patch.avatarSeed !== undefined ||
        patch.avatarFace !== undefined ||
        patch.timeZone !== undefined;
      if (!wantsProfile) return { ok: true };
      if (!identity) return { ok: false, message: 'Sign in first.' };

      const askedAt = Date.now();
      const reply = await send(
        'PUT',
        '/me/profile',
        {
          displayName: patch.displayName ?? identity.displayName,
          // Back to the contract's shape. The number becomes its own digits,
          // so it reads back as the same number.
          // THE FACE THE ACCOUNT WEARS STAYS ON a save that does not name it
          // (a new name); a new color is the initial again (§7).
          avatar: (() => {
            const face =
              patch.avatarFace !== undefined
                ? patch.avatarFace
                : patch.avatarSeed !== undefined
                  ? null
                  : (identity.avatarFace ?? null);
            return face
              ? {
                  kind: 'face' as const,
                  face: face.face,
                  colors: face.colors,
                  seed: String(patch.avatarSeed ?? identity.avatarSeed),
                }
              : {
                  kind: 'generated' as const,
                  seed: String(patch.avatarSeed ?? identity.avatarSeed),
                };
          })(),
          ...(patch.timeZone === undefined ? {} : { timeZone: patch.timeZone }),
        },
        MeView,
      );
      if (reply.status === 'ok') {
        adopt(reply.data, askedAt);
        return { ok: true };
      }
      if (reply.status === 'refused' && reply.code === 'unauthorized') forget();
      return {
        ok: false,
        message: messageOf(
          reply,
          'That did not save.',
          'Could not reach the site. Nothing was saved.',
        ),
      };
    },
    [adopt, forget, identity],
  );

  const signOut = useCallback(async () => {
    // THIS DEVICE STOPS BEING TOLD FIRST (B-191), while the session that owns
    // its push subscription can still drop it: a shared laptop should not keep
    // buzzing with the last person's trade offers.
    if (identity !== null) await forgetThisDevice(identity.userId);
    // The cookie is the session, so the server has to be told. The local
    // copy goes either way: a sign-out that leaves somebody looking signed
    // in because a request failed is the wrong way to fail.
    await send('POST', '/auth/logout', undefined, Nothing);
    forget();
    // And the example leagues go with it. This key used to be part of the
    // session object, so signing out cleared it; moving it out on
    // 2026-09-14 made it survive, and since `demo: false` existed nowhere
    // in the app at the time, that turned "on until you sign out" into
    // "on forever". The hub has a Leave button now, and this is the other
    // half: the next person to use this browser starts clean.
    writeDemo(false);
    setDemo(false);
  }, [forget, identity]);

  const deleteAccount = useCallback<SessionContextValue['deleteAccount']>(
    async (password) => {
      const reply = await send('DELETE', '/me', { password }, Nothing);
      if (reply.status !== 'ok') {
        return {
          ok: false,
          message: messageOf(
            reply,
            'That did not work.',
            'Could not reach the site. Nothing was deleted.',
          ),
          ...(reply.status === 'refused' ? { code: reply.code } : {}),
        };
      }
      forget();
      try {
        // Everything on this device keyed by the person, not the league record.
        localStorage.removeItem(storageKey('chat.read.v1'));
        sessionStorage.clear();
      } catch {
        // Storage blocked: nothing was kept anyway.
      }
      return { ok: true };
    },
    [forget],
  );

  const session = useMemo<Session | null>(
    () => (identity ? { ...identity, demo } : null),
    [identity, demo],
  );

  const value = useMemo(
    () => ({
      session,
      reconciled,
      signUp,
      signIn,
      requestReset,
      confirmReset,
      requestCode,
      signInWithCode,
      signInWithLink,
      changeEmail,
      cancelEmailChange,
      confirmEmailChange,
      undoEmailChange,
      askConfirmLink,
      confirmEmail,
      update,
      signOut,
      deleteAccount,
      forget,
    }),
    [
      session,
      reconciled,
      signUp,
      signIn,
      requestReset,
      confirmReset,
      requestCode,
      signInWithCode,
      signInWithLink,
      changeEmail,
      cancelEmailChange,
      confirmEmailChange,
      undoEmailChange,
      askConfirmLink,
      confirmEmail,
      update,
      signOut,
      deleteAccount,
      forget,
    ],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
